/* eslint-disable react/no-array-index-key */
/* eslint-disable radix */
/* eslint-disable dot-notation */
import React, { useState } from 'react'
import DOMPurify from 'dompurify'
import { useDispatch, useSelector } from 'react-redux'
// import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import { Chip, Typography } from '@material-ui/core'
import Loading from '../helpers/Loading'
import Widget from '../Widget'
import {
  selectMyAlertsData,
  selectMyAlertsStatus,
  acknowledgeAlert,
  updateAlertsHistoryOpen,
} from './MyAlertsSlice'
import myAlertsStyles from './MyAlertsStyles'
import AlertGUI from './Components/AlertGUI'
import DashboardDialog from '../../DashboardComponents/DashboardDialog/DashboardDialog'
import { ErrorMessage } from '../../DashboardComponents/ErrorMessages/ErrorMsg'
import {
  formatAckDate,
  formatRefundDate,
  ensureHttps,
} from '../../../../../utils/utils'
import {
  DOMPurifyAttributeOptions,
  DOMPurifyTagOptions,
} from '../../../../helpers/Alerts/Alert'
import { trackWidgetClick } from '../../../../helpers/trackWidgetClick'

const defaultAlert = {
  id: '',
  datePosted: '',
  dateAcknowledged: '',
  from: '',
  title: '',
  message: '',
  active: '',
}

function MyAlerts({
  className,
  title,
  showNote,
  noteMessage,
  notePosition,
  noteBackground,
  noteIcon,
  noteUrl,
  noteFontColor,
}) {
  const dispatch = useDispatch()
  const widgetData = useSelector(selectMyAlertsData)
  const widgetStatus = useSelector(selectMyAlertsStatus)
  const { alertsHistoryOpen } = useSelector((s) => s.myAlerts)
  const classes = myAlertsStyles()
  const [alert, setAlert] = useState(defaultAlert)

  const dialogHandler = (openAlert = defaultAlert, acknowledge = false) => {
    if (acknowledge) {
      dispatch(acknowledgeAlert(alert))
    }
    setAlert(openAlert)
  }

  return (
    <Widget className={className}>
      <Widget.Header title={title} category="default" />
      <Widget.Content
        className={classes.container}
        showNote={showNote}
        noteMessage={noteMessage}
        notePosition={notePosition}
        noteBackground={noteBackground}
        noteIcon={noteIcon}
        noteUrl={noteUrl}
        noteFontColor={noteFontColor}
      >
        {renderMyAlerts(
          widgetStatus,
          widgetData,
          classes,
          dialogHandler,
          dispatch,
          alertsHistoryOpen,
        )}
      </Widget.Content>
    </Widget>
  )
}

function renderMyAlerts(
  widgetStatus,
  widgetData,
  classes,
  dialogHandler,
  dispatch,
  alertsHistoryOpen,
) {
  switch (widgetStatus) {
    case 'pending':
      return <Loading />
    case 'error':
      return <ErrorMessage />
    case 'fulfilled':
      return (
        <Loaded
          data={widgetData}
          classes={classes}
          dialogHandler={dialogHandler}
          dispatch={dispatch}
          alertsHistoryOpen={alertsHistoryOpen}
        />
      )
    default:
      return <Box />
  }
}

function Loaded({ data, classes, dialogHandler, dispatch, alertsHistoryOpen }) {
  const getAckDateDisplay = (acknowledgedDate) => {
    const dateACK = formatAckDate(acknowledgedDate)
    if (dateACK === '') return 'N/A'
    return dateACK?.toLocaleDateString?.('en-US') ?? 'N/A'
  }
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    if (Number.isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString('en-US')
  }
  const formatSeverityLevelForCards = (level) => {
    const chipClasses = {
      High: classes.alertsHistoryHighChip,
      Medium: classes.alertsHistoryMediumChip,
      Low: classes.alertsHistoryLowChip,
    }
    const chipClass = chipClasses[level]
    if (!chipClass) return 'N/A'
    return (
      <Chip
        classes={{ root: chipClass }}
        size="small"
        label={level}
        clickable={false}
      />
    )
  }
  const alertsHistory = data?.alerts?.alertHistory || []

  return (
    <>
      <DashboardDialog
        open={alertsHistoryOpen}
        dialogTitle={<Typography variant="h3">Past Alert History</Typography>}
        dispatch={dispatch}
        closeDialog={updateAlertsHistoryOpen}
        showTitleCloseButton
        widgetId="MyAlerts"
        contentClassName={classes.alertsHistoryDialogContentBg}
        dialogContent={
          <Box className={classes.alertsHistoryDialogContentWrapper}>
            <AlertsHistoryCards
              alerts={alertsHistory}
              classes={classes}
              getAckDateDisplay={getAckDateDisplay}
              formatSeverityLevel={formatSeverityLevelForCards}
              formatDateForDisplay={formatDateForDisplay}
              dispatch={dispatch}
            />
          </Box>
        }
        maxWidth="100%"
      />
      <AlertGUI data={data} classes={classes} dialogHandler={dialogHandler} />
    </>
  )
}

const AlertsHistoryCards = ({
  alerts,
  classes,
  getAckDateDisplay,
  formatSeverityLevel,
  formatDateForDisplay,
  dispatch,
}) => (
  <Box
    className={classes.alertsHistoryCardContainer}
    role="list"
    aria-label="Past Alerts History"
  >
    {alerts?.map((item, index) => {
      const startDate =
        item[`start_date`] ||
        item[`sent_date`] ||
        (item[`lastUpdated`] ? formatRefundDate(item[`lastUpdated`]) : '')
      const endDate = item[`end_date`] || item[`exp_date`] || ''
      const dateRangeStr = startDate
        ? `${formatDateForDisplay(startDate)} - ${
            endDate ? formatDateForDisplay(endDate) : 'N/A'
          }`
        : 'N/A'
      return (
        <Paper
          key={item?.notificationId || item?.dockey || `alert-${index}`}
          className={classes.alertsHistoryCard}
          role="listitem"
          elevation={1}
        >
          <Box className={classes.alertsHistoryCardHeader}>
            <Typography variant="h6">
              {item?.title || 'Untitled Alert'}
            </Typography>
          </Box>
          <Box className={classes.alertsHistoryCardBody}>
            <Typography
              variant="body2"
              className={classes.alertsHistoryDateRow}
            >
              {dateRangeStr}
            </Typography>
            <Box className={classes.alertsHistoryMessage}>
              <div
                // eslint-disable-next-line react/no-danger
                className={classes.descriptionStyle}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(item[`message`] || 'N/A', {
                    ADD_TAGS: DOMPurifyTagOptions,
                    ADD_ATTR: DOMPurifyAttributeOptions,
                  }),
                }}
              />
            </Box>
            {(item.link || item[`link`]) && (
              <Box className={classes.alertsHistoryLinkRow}>
                <a
                  href={item.link || item[`link`]}
                  rel="noopener noreferrer"
                  target="_blank"
                  className={classes.alertsHistoryLink}
                  onClick={() =>
                    trackWidgetClick(
                      dispatch,
                      'MyAlerts',
                      item?.title || 'Past Alert Link',
                    )
                  }
                >
                  {ensureHttps(item.link || item[`link`])}
                </a>
              </Box>
            )}
          </Box>
          <Box className={classes.alertsHistoryFooter}>
            <Box className={classes.alertsHistoryFooterItem}>
              <Typography variant="body2">
                <strong>Type:</strong> {item[`alerttype`] || 'N/A'}
              </Typography>
            </Box>
            <Box className={classes.alertsHistoryFooterItem}>
              <Typography variant="body2" component="span">
                <strong>Security Level:</strong>{' '}
              </Typography>
              {formatSeverityLevel(item[`severityLevel`])}
            </Box>
            <Box className={classes.alertsHistoryFooterItem}>
              <Typography variant="body2">
                <strong>Acknowledged Time:</strong>{' '}
                {getAckDateDisplay(item[`acknowledgedTS`])}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )
    })}
  </Box>
)

export default MyAlerts
